import { useEffect, useRef } from 'react';
import { AGENT_OTP_LENGTH, parseOtpDigits } from './otpInputUtils';

interface OTPCredential extends Credential {
  code: string;
}

/**
 * Return the current page hostname for WebOTP SMS origin binding.
 */
export function getWebOtpHost(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname.toLowerCase();
}

/**
 * Listen for an incoming SMS OTP via the WebOTP API and invoke onCode when received.
 */
export function useWebOtpAutofill(enabled: boolean, onCode: (code: string) => void): void {
  const onCodeRef = useRef(onCode);
  useEffect(() => { onCodeRef.current = onCode; }, [onCode]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }
    if (!navigator.credentials?.get || !('OTPCredential' in window)) {
      return undefined;
    }

    const controller = new AbortController();
    void navigator.credentials.get({
      otp: { transport: ['sms'] },
      signal: controller.signal,
    } as CredentialRequestOptions).then((credential) => {
      if (!credential || !('code' in credential)) {
        return;
      }
      const code = parseOtpDigits(String((credential as OTPCredential).code), AGENT_OTP_LENGTH);
      if (code.length === AGENT_OTP_LENGTH) {
        onCodeRef.current(code);
      }
    }).catch(() => {
      // User dismissed the prompt or the browser does not support WebOTP on this device.
    });

    return () => controller.abort();
  }, [enabled]);
}
